import React, { useCallback, useState } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { ReactComponent as Upload } from '../../resources/icons/upload.svg';
import { ReactComponent as User } from '../../resources/icons/user.svg';
import { preventCachedImage } from '../../utils/helperFunctions';

interface ImageUploadProps {
  userImage?: string;
  onBase64Image: (base64: string) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  userImage,
  onBase64Image,
}) => {
  const [error, setError] = useState('');

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const file = acceptedFiles[0];

      if (fileRejections.length > 0) {
        setError('Invalid file format. Only JPG, PNG, SVG allowed.');
        return;
      }

      if (!file) return;

      const reader = new FileReader();

      reader.onloadend = () => {
        if (reader.result && typeof reader.result === 'string') {
          const img = new Image();
          img.src = reader.result;

          img.onload = () => {
            if (img.width > 2000 || img.height > 1400) {
              setError(
                `Image is ${img.width}x${img.height}. Must be 2000x1400px or smaller.`
              );
              return;
            }

            setError('');
            onBase64Image(reader.result as string);
          };

          img.onerror = () => {
            setError('Failed to load image.');
          };
        }
      };

      reader.readAsDataURL(file);
    },
    [onBase64Image]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/svg+xml': ['.svg'],
    },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className="bg-[#F9FAFB] w-full p-4 text-center cursor-pointer"
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center gap-2">
          {userImage ? (
            <img
              src={preventCachedImage(userImage)}
              alt="Profile preview"
              className="w-[64px] h-[64px] object-cover rounded-full"
            />
          ) : (
            <User />
          )}
          <p className="mt-1">Add profile picture</p>
          <p className="text-sm text-gray-500 mt-1">
            SVG, PNG or JPG (max 2000x1400px)
          </p>
          {error && (
            <p className="text-red-500 text-sm mt-2 text-center">{error}</p>
          )}
          <div className="flex">
            <Upload />
            <p className="ml-1 text-[#1672EC]">Upload image</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
